import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { tenantQueries } from "@surgia/api/src/queryStore/shared/tenant";
import CommonProductsTable from "../../../containers/tables/commonProducts-table";
import TableToolbar from "@surgia/ui/containers/tableToolBar";
import { GenericTableHeader } from "@surgia/ui";
import ProductAssignmentLayout from "../layout";
import DetailsConfiguration from "../steps/DetailsConfiguration";
import CustomerView from "../steps/customerView";
import Button from "@surgia/ui/components/button";
import GenericModal from "@surgia/ui/components/genericModal";
import { filterInitialData } from "../constant";
import ProductFiltersModal from "../../../containers/productFilterModal";
import { ProductFilters } from "../../../containers/productFilterModal/constants";
import { useProductAssignment } from "../hooks";
const ProductAssignment: React.FC = () => {
  const { t } = useTranslation();
  const { id: tenantId } = useParams();
  const navigate = useNavigate();
  // states
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedProducts] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [search, setSearch] = useState("");
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [filters, setFilters] = useState<ProductFilters>(filterInitialData);

  // Add sorting state
  const [sortState, setSortState] = useState<{
    column: string | null;
    direction: "asc" | "desc" | null;
  }>({
    column: "id", // Initial sort column
    direction: "desc", // Initial sort direction (desc for newest first)
  });
  // track all the clicked parent products
  const [toggledProductsApiTracker, setToggledProductsApiTracker] = useState<
    any[]
  >([]);
  // track only the assigned products toggled now
  const [selectedAssignedProducts, setSelectedAssignedProducts] = useState<
    any[]
  >([]);

  // Reset function to clear all states
  const resetStates = () => {
    setToggledProductsApiTracker([]);
    setSelectedAssignedProducts([]);
    setCurrentPage(0);
    setSearch("");
    setFilters(filterInitialData);
  };

  // Add sort handler
  const handleSort = (newSortState: {
    column: string | null;
    direction: "asc" | "desc" | null;
  }) => {
    setSortState(newSortState);
    setCurrentPage(0);
  };

  // queries
  const { data: commonProductsResponse, isLoading: isCommonProductsLoading } =
    useQuery({
      ...tenantQueries.queries.commonProducts({
        component_id: import.meta.env.VITE_PUBLIC_COMPONENT_ID,
        tenant_id: tenantId ? +tenantId : undefined,
        name: search,
        page: currentPage,
        size: 10,
        id: filters?.id ? filters.id : undefined,
        price_from: filters?.price_from ? filters.price_from : undefined,
        price_to: filters?.price_to ? filters.price_to : undefined,
        categories: filters?.category ? filters.category : undefined,
        brand_name: filters?.brandName ? filters.brandName : undefined,
        // Add sorting parameter
        sort:
          sortState.column && sortState.direction
            ? `${sortState.column},${sortState.direction}`
            : undefined,
      }),
      select: (data) => {
        return {
          total: data?.total,
          data: data.data.map((item) => ({
            ...item,
            status: item?.status === "A",
            brand: {
              surgiaBrandName: item?.product_brands?.[0]?.brand_name,
              surgiaBrandImage: item?.product_brands?.[0]?.brand_url,
            },
            category: {
              surgiaFirstCategory:
                item?.product_categories?.[0]?.category?.name,
              surgiaSecondCategory:
                item?.product_categories?.[0]?.category?.parent_category?.name,
              surgiaThirdCategory:
                item?.product_categories?.[0]?.category?.parent_category
                  ?.parent_category?.name,
            },
            variation: item.variants_count,
            sku: {
              surgiaProductSKU: item?.sku,
              assigned: item?.is_assigned,
              id: item?.id,
            },
            name: {
              surgiaProductName: item?.name,
              surgiaProductImage: item?.product_images?.[0]?.image_path,
            },
            variantsCount: item?.variants_count,
          })),
        };
      },
      placeholderData: keepPreviousData,
    });

  //helpers
  const isCurrentlyAssigned = (id: string) =>
    selectedAssignedProducts.includes(id);
  // hooks
  const {
    handlePreviousStep,
    handleNextStep,
    handleSaveAsDraft,
    handleConfirmGoBack,
    handleCancelGoBack,
    showConfirmModal,
    isAssigning,
  } = useProductAssignment({
    toggledProductsApiTracker,
    currentStep,
    setCurrentStep,
    tenantId: tenantId!,
    currentPage,
    search,
    filters,
    resetStates,
  });
  const handleFinish = () => {
    navigate(-1);
    toast.success("changes saved successfully");
  };
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="flex-1 p-6 bg-white">
            <div className="bg-white rounded-xl border border-gray-200 pt-4">
              <div className="pt-4">
                <TableToolbar
                  statusTabs={[]}
                  currentStatus={""}
                  onStatusChange={() => {}}
                  searchValue={search}
                  onSearchChange={setSearch}
                  onFilterClick={() => setFiltersModalOpen(true)}
                  children={
                    <GenericTableHeader
                      titleTranslationKey="shared.sidebar.catalog"
                      totalCount={commonProductsResponse?.total || 0}
                      showBadge={true}
                      customBadgeText={`${commonProductsResponse?.total || 0}`}
                      showBorder={false}
                      padding=""
                      layout="basic"
                      badgeClassName="!bg-transparent !rounded-sm !border-[#D5D7DA]"
                    />
                  }
                />
              </div>
              <div className="overflow-auto">
                <CommonProductsTable
                  data={commonProductsResponse?.data || []}
                  loading={isCommonProductsLoading}
                  currentPage={currentPage}
                  totalPages={Math.ceil(
                    (commonProductsResponse?.total || 0) / 10
                  )}
                  onPageChange={setCurrentPage}
                  isCurrentlyAssigned={isCurrentlyAssigned}
                  selectedAssignedProducts={selectedAssignedProducts}
                  toggledProductsApiTracker={toggledProductsApiTracker}
                  setToggledProductsApiTracker={setToggledProductsApiTracker}
                  setSelectedAssignedProducts={setSelectedAssignedProducts}
                  sortState={sortState}
                  onSort={handleSort}
                />
              </div>
            </div>
            <div className="pt-6 flex justify-between items-center bg-white">
              <span className="text-sm text-gray-600">
                {selectedAssignedProducts?.length}{" "}
                {t(
                  "admin.productAssignment.content.selectProducts.productsSelected"
                )}
              </span>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  onClick={handlePreviousStep}
                  disabled={currentStep === 1}
                >
                  {t("admin.productAssignment.actions.previous")}
                </Button>

                {currentStep < 3 && (
                  <Button
                    variant="outline"
                    onClick={async () => {
                      try {
                        await handleNextStep();
                      } catch (error) {
                        console.error("Error proceeding to next step:", error);
                      }
                    }}
                    disabled={
                      isAssigning || selectedAssignedProducts?.length === 0
                    }
                  >
                    {isAssigning
                      ? "Assigning..."
                      : t("admin.productAssignment.actions.next")}
                  </Button>
                )}
                {currentStep > 2 && (
                  <Button variant="primary" onClick={handleFinish}>
                    {t("admin.productAssignment.actions.finish")}
                  </Button>
                )}
              </div>
            </div>
            <ProductFiltersModal
              open={filtersModalOpen}
              onClose={() => setFiltersModalOpen(false)}
              onApply={() => {
                setFiltersModalOpen(false);
              }}
              filters={filters}
              setFilters={setFilters}
              tenantId={tenantId!}
              showPriceRange={false}
            />
          </div>
        );
      case 2:
        return (
          <DetailsConfiguration
            selectedProducts={selectedAssignedProducts}
            onPreviousStep={handlePreviousStep}
            onNextStep={handleNextStep}
            isAssigning={isAssigning}
          />
        );
      case 3:
        return (
          <CustomerView
            selectedProducts={selectedAssignedProducts}
            onPreviousStep={handlePreviousStep}
            onFinish={handleFinish}
          />
        );
      default:
        return null;
    }
  };

  return (
    <ProductAssignmentLayout
      currentStep={currentStep}
      selectedProductsCount={selectedProducts.length}
      onPreviousStep={handlePreviousStep}
      onNextStep={handleNextStep}
      onFinish={handleFinish}
      onSaveAsDraft={handleSaveAsDraft}
      isAssigning={isAssigning}
    >
      {renderStepContent()}

      {/* Confirmation Modal for going back from step 1 */}
      <GenericModal
        open={showConfirmModal}
        onClose={handleCancelGoBack}
        title={t("admin.productAssignment.messages.saveProgressGoBack")}
        subtitle={t("admin.productAssignment.messages.saveProgressSubtitle")}
        footer={
          <>
            <Button variant="outline" onClick={handleCancelGoBack}>
              {t("admin.productAssignment.messages.stayHere")}
            </Button>
            <Button variant="primary" onClick={handleConfirmGoBack}>
              {t("admin.productAssignment.messages.saveAndGoBack")}
            </Button>
          </>
        }
      >
        <div className="text-gray-600">
          <p>{t("admin.productAssignment.messages.goingBackMessage")}</p>
        </div>
      </GenericModal>
    </ProductAssignmentLayout>
  );
};

export default ProductAssignment;
